import React, { useState } from 'react';
import { 
  Container, Typography, Box, Grid, TextField, Button, 
  Stack, MenuItem, InputAdornment, Paper, Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  LocationOn, WhatsApp, 
  Send, Person, Topic, Business, Hub, PrecisionManufacturing, RocketLaunch
} from '@mui/icons-material';
import { companyName, address, gst, contacts, whatsappUrl } from '../store/contactInfo';
import { submitContactForm } from '../services/api';

const ORANGE = '#FF6B00';
const BLACK = '#121212';
const WHITE = '#FFFFFF';

// Animation Variants
const float = {
  animate: {
    y: [0, -20, 0],
    rotate: [0, 5, 0],
    transition: { duration: 6, repeat: Infinity, ease: "easeInOut" }
  }
};

const pulse = {
  animate: {
    scale: [1, 1.05, 1],
    opacity: [0.5, 1, 0.5],
    transition: { duration: 3, repeat: Infinity, ease: "easeInOut" }
  }
};

export default function Contact() {
  const [name, setName] = useState('');
  const [requirementType, setRequirementType] = useState('Storage');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await submitContactForm({ name, requirementType, message });
      setSuccess(true);
      setName('');
      setMessage('');
      setRequirementType('Storage');
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: WHITE, minHeight: '100vh' }}>
      
      {/* --- PREMIUM DYNAMIC HERO --- */}
      <Box sx={{ 
        position: 'relative', 
        pt: { xs: 6, md: 12}, 
        pb: { xs: 10, md: 18 },
        overflow: 'hidden',
        background: `radial-gradient(circle at 20% 20%, #fefefe 0%, #f5f5f5 100%)`
      }}>
        {/* Abstract Tech Background */}
        <Box 
          component={motion.div}
          variants={float}
          animate="animate"
          sx={{ 
            position: 'absolute', right: '-5%', top: '10%', 
            color: `${ORANGE}15`, zIndex: 0,
            display: { xs: 'none', md: 'block' }
          }}
        >
          <Hub sx={{ fontSize: '30rem' }} />
        </Box>

        {/* Floating Background Orbs */}
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              x: [0, 30, 0],
              y: [0, 50, 0],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              ease: "linear"
            }}
            sx={{
              position: 'absolute',
              width: 10 + i * 10,
              height: 10 + i * 10,
              borderRadius: '50%',
              border: `2px solid ${ORANGE}33`,
              left: `${10 + i * 20}%`,
              top: `${20 + i * 15}%`,
              zIndex: 0
            }}
          />
        ))}

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3 }}>
                  <Box 
                    component={motion.div}
                    variants={pulse}
                    animate="animate"
                    sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#4CAF50', boxShadow: '0 0 15px #4CAF50' }} 
                  />
                  <Typography variant="caption" sx={{ fontWeight: 900, letterSpacing: 3, color: 'text.secondary' }}>
                    ENGINEERS ON STANDBY • READY TO BUILD
                  </Typography>
                </Stack>
                
                <Typography variant="h1" sx={{ 
                  fontWeight: 900, 
                  fontSize: { xs: '2.25rem', sm: '3rem', md: '5.5rem' }, 
                  lineHeight: { xs: 1.1, md: 0.9 }, 
                  mb: 3, 
                  color: BLACK 
                }}>
                  <motion.span
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    style={{ display: 'inline-block' }}
                  >
                    Your Vision,
                  </motion.span> 
                  <br />
                  <motion.span 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 100, delay: 0.5 }}
                    style={{ 
                      display: 'inline-block',
                      color: WHITE, 
                      WebkitTextStroke: `2px ${BLACK}`,
                      textShadow: `6px 6px ${ORANGE}`,
                      cursor: 'pointer'
                    }}
                    whileHover={{ skewX: -10, scale: 1.05 }}
                  >
                    Our Logic.
                  </motion.span>
                </Typography>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <Typography variant="h6" sx={{ color: 'grey.600', fontWeight: 400, maxWidth: 550, mb: 4, lineHeight: 1.6, fontSize: { xs: '0.95rem', md: '1.25rem' } }}>
                    Transforming industrial spaces with world-class racking and automation. 
                    Reach out to the Divine Technovations core team today.
                  </Typography>

                  <Stack direction="row" spacing={2}>
                     <Box component={motion.div} whileHover={{ y: -5 }}>
                        <RocketLaunch sx={{ color: ORANGE, fontSize: 40 }} />
                     </Box>
                     <Box component={motion.div} whileHover={{ y: -5 }} transition={{ delay: 0.1 }}>
                        <PrecisionManufacturing sx={{ color: BLACK, fontSize: 40 }} />
                     </Box>
                  </Stack>
                </motion.div>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* --- CENTERED BOXES --- */}
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -8 }, mb: { xs: 8, md: 12 }, position: 'relative', zIndex: 2, px: { xs: 2, sm: 3 } }}>
        <Grid container spacing={3} justifyContent="center">
          {contacts.map((person, i) => (
            <Grid item xs={12} sm={6} md={3} key={person.email}>
              <Paper
                elevation={10}
                component={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.1 }}
                whileHover={{ y: -10, transition: { duration: 0.2 } }}
                sx={{
                  p: 4, textAlign: 'center', borderRadius: '24px',
                  bgcolor: WHITE, border: '1px solid #eee',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.05)', height: '100%'
                }}
              >
                <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: '16px', bgcolor: BLACK, color: ORANGE, mb: 2 }}>
                  <Person />
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'grey.400', display: 'block', mb: 1 }}>
                  CONTACT PERSON
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 800, color: BLACK, mb: 1 }}>
                  {person.name}
                </Typography>
                <Typography
                  component="a"
                  href={`tel:+91${person.phone}`}
                  sx={{ display: 'block', fontWeight: 700, color: ORANGE, textDecoration: 'none', mb: 0.5 }}
                >
                  {person.phoneDisplay}
                </Typography>
                <Typography
                  component="a"
                  href={`mailto:${person.email}`}
                  sx={{ display: 'block', fontWeight: 600, color: 'grey.600', textDecoration: 'none', fontSize: '0.9rem', wordBreak: 'break-word' }}
                >
                  {person.email}
                </Typography>
              </Paper>
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={10}
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              sx={{
                p: 4, textAlign: 'center', borderRadius: '24px',
                bgcolor: WHITE, border: '1px solid #eee',
                boxShadow: '0 20px 60px rgba(0,0,0,0.05)', height: '100%'
              }}
            >
              <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: '16px', bgcolor: BLACK, color: ORANGE, mb: 2 }}>
                <Business />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'grey.400', display: 'block', mb: 1 }}>
                ADDRESS
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: BLACK, lineHeight: 1.6 }}>
                {companyName}
                <br />
                {address.line1}
                <br />
                {address.line2}
                <br />
                {address.line3}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={10}
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              sx={{
                p: 4, textAlign: 'center', borderRadius: '24px',
                bgcolor: WHITE, border: '1px solid #eee',
                boxShadow: '0 20px 60px rgba(0,0,0,0.05)', height: '100%'
              }}
            >
              <Box sx={{ display: 'inline-flex', p: 1.5, borderRadius: '16px', bgcolor: BLACK, color: ORANGE, mb: 2 }}>
                <LocationOn />
              </Box>
              <Typography variant="caption" sx={{ fontWeight: 800, color: 'grey.400', display: 'block', mb: 1 }}>
                GST VERIFICATION
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 800, color: BLACK, wordBreak: 'break-word' }}>
                {gst}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* --- CLEAN CENTERED FORM --- */}
      <Container maxWidth="sm" sx={{ pb: { xs: 8, md: 15 }, px: { xs: 2, sm: 3 } }}>
        <Box 
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          sx={{ textAlign: 'center' }}
        >
          <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, fontSize: { xs: '1.75rem', md: '3rem' } }}>Get a Quote</Typography>
          <Typography sx={{ color: 'grey.500', mb: 3 }}>Typically responds in under 2 hours.</Typography>
          {success ? <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>Thank you! Your message has been received. Our team will get back to you shortly.</Alert> : null}
          {error ? <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{error}</Alert> : null}

          <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField 
              fullWidth label="Full Name" variant="outlined" required
              value={name}
              onChange={(e) => setName(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{color: ORANGE}}/></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
            <TextField 
              fullWidth label="Requirement Type" select variant="outlined" required
              value={requirementType}
              onChange={(e) => setRequirementType(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Topic sx={{color: ORANGE}}/></InputAdornment> }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            >
              <MenuItem value="Storage">Storage Solutions</MenuItem>
              <MenuItem value="Automation">Warehouse Automation</MenuItem>
              <MenuItem value="Other">General Inquiry</MenuItem>
            </TextField>
            <TextField 
              fullWidth label="Tell us about your project" multiline rows={4} variant="outlined" required
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
            
            <Button 
              type="submit"
              variant="contained" 
              size="large"
              disabled={submitting}
              endIcon={<Send />}
              sx={{ 
                py: 2.5, bgcolor: ORANGE, color: WHITE, borderRadius: '12px', 
                fontWeight: 900, fontSize: '1.1rem',
                boxShadow: `0 10px 30px ${ORANGE}44`,
                '&:hover': { bgcolor: BLACK, boxShadow: 'none' }
              }}
            >
              {submitting ? 'SENDING...' : 'SEND MESSAGE'}
            </Button>

            <Button 
              component="a"
              href={whatsappUrl("Hello, I'd like to discuss a storage requirement.")}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<WhatsApp />}
              sx={{ color: BLACK, fontWeight: 700, mt: 2 }}
            >
              Chat with Sales on WhatsApp
            </Button>
          </Stack>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}